/* PersonalHealthManager — централен hub за conditions / symptoms / family
 * history / food recommendations / multi-bot AI / lab OCR. (iter420.3 Phase 3)
 *
 * Tabs: Заболявания | Симптоми | Фамилия | Препоръки | AI анализ | Лаборатории
 *
 * GDPR: при първи опит за save → отваря <GdprConsentDialog />. След съгласие
 * profile е активиран и всички action са разрешени.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Heart, Activity, Users, Leaf, Brain, FlaskConical, Plus, Trash2, Loader2,
  AlertCircle, CheckCircle2, ThumbsUp, ThumbsDown, Upload, Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import GdprConsentDialog from "@/components/GdprConsentDialog";

const TABS = [
  { id: "conditions", icon: Heart,        bg: "Заболявания",   en: "Conditions" },
  { id: "symptoms",   icon: Activity,     bg: "Симптоми",      en: "Symptoms" },
  { id: "family",     icon: Users,        bg: "Фамилия",       en: "Family" },
  { id: "foods",      icon: Leaf,         bg: "Препоръки",     en: "Food" },
  { id: "ai",         icon: Brain,        bg: "AI анализ",     en: "AI analysis" },
  { id: "labs",       icon: FlaskConical, bg: "Лаборатории",   en: "Labs" },
];

const SYMPTOMS = [
  { id: "fatigue",        bg: "Умора" },
  { id: "bloating",       bg: "Подуване" },
  { id: "constipation",   bg: "Запек" },
  { id: "diarrhea",       bg: "Диария" },
  { id: "hair_loss",      bg: "Косопад" },
  { id: "anxiety",        bg: "Тревожност" },
  { id: "insomnia",       bg: "Безсъние" },
  { id: "headache",       bg: "Главоболие" },
  { id: "joint_pain",     bg: "Болки в ставите" },
  { id: "acid_reflux",    bg: "Киселини" },
  { id: "low_libido",     bg: "Ниско либидо" },
  { id: "irregular_cycle",bg: "Нередовен цикъл" },
  { id: "acne",           bg: "Акне" },
  { id: "brain_fog",      bg: "Мъгла в главата" },
  { id: "low_energy",     bg: "Ниска енергия" },
];

const RELATIONS = [
  { id: "mother",                 bg: "Майка" },
  { id: "father",                 bg: "Баща" },
  { id: "sibling",                bg: "Брат/сестра" },
  { id: "grandparent_maternal",   bg: "Баба/дядо (от майка)" },
  { id: "grandparent_paternal",   bg: "Баба/дядо (от баща)" },
  { id: "child",                  bg: "Дете" },
];

export default function PersonalHealthManager() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [tab, setTab] = useState("conditions");
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [showConsent, setShowConsent] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [pickCondition, setPickCondition] = useState("");
  const [pickSeverity, setPickSeverity] = useState("moderate");
  const [pickSymptom, setPickSymptom] = useState("");
  const [symptomSeverity, setSymptomSeverity] = useState(5);
  const [pickRelation, setPickRelation] = useState("mother");
  const [familyCondition, setFamilyCondition] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, catRes] = await Promise.all([
        axios.get(`${API}/health-intelligence/profile`),
        axios.get(`${API}/health-intelligence/conditions-catalog`),
      ]);
      setProfile(profRes.data);
      setCatalog(catRes.data.conditions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при зареждане");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const hasConsent = profile?.consent?.granted === true;

  const requireConsent = (fn) => async (...args) => {
    if (!hasConsent) { setShowConsent(true); return; }
    return fn(...args);
  };

  const handleAddCondition = requireConsent(async () => {
    if (!pickCondition) { toast.error(bg ? "Избери състояние" : "Pick a condition"); return; }
    try {
      await axios.post(`${API}/health-intelligence/conditions`, {
        condition_id: pickCondition, severity: pickSeverity,
      });
      toast.success(bg ? "Добавено ✓" : "Added ✓");
      setPickCondition("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Грешка"); }
  });

  const handleDeleteCondition = async (id) => {
    if (!window.confirm(bg ? "Изтрий?" : "Delete?")) return;
    await axios.delete(`${API}/health-intelligence/conditions/${id}`);
    toast.success(bg ? "Изтрито" : "Deleted");
    fetchAll();
  };

  const handleAddSymptom = requireConsent(async () => {
    if (!pickSymptom) { toast.error(bg ? "Избери симптом" : "Pick a symptom"); return; }
    try {
      await axios.post(`${API}/health-intelligence/symptoms`, {
        symptom_id: pickSymptom, severity: Number(symptomSeverity),
      });
      toast.success(bg ? "Записан ✓" : "Logged ✓");
      setPickSymptom("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Грешка"); }
  });

  const handleAddFamily = requireConsent(async () => {
    if (!familyCondition) { toast.error(bg ? "Избери състояние" : "Pick a condition"); return; }
    try {
      await axios.post(`${API}/health-intelligence/family-history`, {
        relation: pickRelation, condition_id: familyCondition,
      });
      toast.success(bg ? "Добавено ✓" : "Added ✓");
      setFamilyCondition("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Грешка"); }
  });

  const handleDeleteFamily = async (id) => {
    if (!window.confirm(bg ? "Изтрий?" : "Delete?")) return;
    await axios.delete(`${API}/health-intelligence/family-history/${id}`);
    toast.success(bg ? "Изтрито" : "Deleted");
    fetchAll();
  };

  const conditionById = (id) => catalog.find((c) => c.id === id);

  if (loading) {
    return <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="personal-health-manager">
      {/* Tabs */}
      <div className={`flex gap-1 overflow-x-auto pb-1 ${isDark ? "border-b border-slate-800" : "border-b border-slate-200"}`}>
        {TABS.map(({ id, icon: Icon, bg: bgL, en }) => (
          <button key={id}
                  onClick={() => setTab(id)}
                  data-testid={`phm-tab-${id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold whitespace-nowrap rounded-t-lg transition-colors ${
                    tab === id
                      ? (isDark ? "bg-violet-500/20 text-violet-200 border-b-2 border-violet-400" : "bg-violet-50 text-violet-700 border-b-2 border-violet-500")
                      : (isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")
                  }`}>
            <Icon className="w-3.5 h-3.5" />
            {bg ? bgL : en}
          </button>
        ))}
      </div>

      {/* Consent reminder if missing */}
      {!hasConsent && (
        <div className={`p-3 rounded-xl flex items-start gap-3 ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
          <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`} />
          <div className="flex-1">
            <p className={`text-[11.5px] leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              {bg
                ? "За да активираш Personal Health модулите, нужно е GDPR съгласие. Това е едноразово действие — отнема 30 секунди."
                : "To activate Personal Health modules, GDPR consent is required. One-time, takes 30 seconds."}
            </p>
            <Button onClick={() => setShowConsent(true)} size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="phm-open-consent">
              {bg ? "Преглед на съгласието" : "Review consent"}
            </Button>
          </div>
        </div>
      )}

      {/* CONDITIONS TAB */}
      {tab === "conditions" && (
        <div className="space-y-3" data-testid="phm-conditions-tab">
          <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
            <CardContent className="p-4 space-y-3">
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "Добави състояние" : "Add condition"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-6">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Състояние" : "Condition"}
                  </label>
                  <Select value={pickCondition} onValueChange={setPickCondition}>
                    <SelectTrigger data-testid="phm-cond-select"
                                   className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}>
                      <SelectValue placeholder={bg ? "Избери..." : "Pick..."} />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}>
                      {catalog.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{bg ? c.name_bg : c.name_en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Сила" : "Severity"}
                  </label>
                  <Select value={pickSeverity} onValueChange={setPickSeverity}>
                    <SelectTrigger className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}>
                      <SelectItem value="mild">{bg ? "Леко" : "Mild"}</SelectItem>
                      <SelectItem value="moderate">{bg ? "Умерено" : "Moderate"}</SelectItem>
                      <SelectItem value="severe">{bg ? "Изразено" : "Marked"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3 flex items-end">
                  <Button onClick={handleAddCondition} data-testid="phm-cond-add"
                          className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white">
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    {bg ? "Добави" : "Add"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {(profile?.conditions || []).map((c) => {
              const meta = conditionById(c.condition_id);
              return (
                <Card key={c.id} className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
                      data-testid={`phm-cond-${c.id}`}>
                  <CardContent className="p-3 flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-500 text-white">
                      <Heart className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xs font-bold">{meta ? (bg ? meta.name_bg : meta.name_en) : c.condition_id}</h4>
                      <p className={`text-[10.5px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {meta?.category} {c.severity && `· ${c.severity}`}
                      </p>
                      {meta?.explainer_bg && (
                        <p className={`text-[11px] mt-1.5 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          {bg ? meta.explainer_bg : meta.explainer_en}
                        </p>
                      )}
                    </div>
                    <Button onClick={() => handleDeleteCondition(c.id)} variant="ghost" size="icon"
                            className="h-7 w-7" data-testid={`phm-cond-del-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {(profile?.conditions || []).length === 0 && (
              <p className={`text-xs text-center py-6 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Все още няма добавени състояния." : "No conditions added yet."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* SYMPTOMS TAB */}
      {tab === "symptoms" && (
        <div className="space-y-3" data-testid="phm-symptoms-tab">
          <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
            <CardContent className="p-4 space-y-3">
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "Въведи симптом" : "Add a symptom"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-6">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Симптом" : "Symptom"}
                  </label>
                  <Select value={pickSymptom} onValueChange={setPickSymptom}>
                    <SelectTrigger data-testid="phm-symp-select"
                                   className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}>
                      <SelectValue placeholder={bg ? "Избери от списъка..." : "Pick from list..."} />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}>
                      {SYMPTOMS.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Сила (0-10)" : "Severity (0-10)"}
                  </label>
                  <Input type="number" min="0" max="10" value={symptomSeverity}
                         onChange={(e) => setSymptomSeverity(e.target.value)}
                         className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                         data-testid="phm-symp-sev" />
                </div>
                <div className="sm:col-span-3 flex items-end">
                  <Button onClick={handleAddSymptom} data-testid="phm-symp-add"
                          className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white">
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    {bg ? "Запиши" : "Save"}
                  </Button>
                </div>
              </div>
              <p className={`text-[10.5px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Скала: 0 = няма, 5 = умерено, 10 = много силно" : "Scale: 0 = none, 5 = moderate, 10 = very strong"}
              </p>
            </CardContent>
          </Card>

          <div className="space-y-1.5">
            {(profile?.symptoms || []).slice().reverse().slice(0, 30).map((s) => {
              const sm = SYMPTOMS.find((x) => x.id === s.symptom_id);
              return (
                <div key={s.id} className={`flex items-center gap-2 px-3 py-1.5 rounded ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
                  <Activity className="w-3.5 h-3.5 text-violet-500" />
                  <span className={`text-xs font-medium flex-1 ${isDark ? "text-white" : "text-slate-900"}`}>{sm?.bg || s.symptom_id}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        s.severity >= 7 ? "bg-rose-100 text-rose-700"
                                        : s.severity >= 4 ? "bg-amber-100 text-amber-700"
                                                          : "bg-emerald-100 text-emerald-700"
                      }`}>
                    {s.severity}/10
                  </span>
                  <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                    {new Date(s.logged_at).toLocaleDateString(bg ? "bg-BG" : "en-US")}
                  </span>
                </div>
              );
            })}
            {(profile?.symptoms || []).length === 0 && (
              <p className={`text-xs text-center py-6 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Все още няма въведени симптоми." : "No symptoms entered yet."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* FAMILY TAB */}
      {tab === "family" && (
        <div className="space-y-3" data-testid="phm-family-tab">
          <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
            <CardContent className="p-4 space-y-3">
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "Добави фамилна анамнеза" : "Add family history"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-4">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Роднина" : "Relation"}
                  </label>
                  <Select value={pickRelation} onValueChange={setPickRelation}>
                    <SelectTrigger data-testid="phm-fam-rel"
                                   className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}>
                      {RELATIONS.map((r) => <SelectItem key={r.id} value={r.id}>{r.bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-5">
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Състояние" : "Condition"}
                  </label>
                  <Select value={familyCondition} onValueChange={setFamilyCondition}>
                    <SelectTrigger data-testid="phm-fam-cond"
                                   className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}>
                      <SelectValue placeholder={bg ? "Избери състояние..." : "Pick a condition..."} />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}>
                      {catalog.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{bg ? c.name_bg : c.name_en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3 flex items-end">
                  <Button onClick={handleAddFamily} data-testid="phm-fam-add"
                          className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white">
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    {bg ? "Добави" : "Add"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className={`p-3 rounded-lg ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
            <p className={`text-[10.5px] leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              💡 {bg
                ? "Семейната анамнеза е област за подобрение и проследяване — не повод за тревожност. Показваме само образователна информация и хранителни препоръки."
                : "Family history is an area for improvement and monitoring — not a cause for alarm. We show only educational info and dietary suggestions."}
            </p>
          </div>

          <div className="space-y-2">
            {(profile?.family_history || []).map((f) => {
              const meta = conditionById(f.condition_id);
              const rel = RELATIONS.find((r) => r.id === f.relation);
              return (
                <Card key={f.id} className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
                  <CardContent className="p-3 flex items-center gap-2.5">
                    <Users className="w-4 h-4 text-violet-500" />
                    <div className="flex-1">
                      <span className="text-xs font-bold">{rel?.bg || f.relation}</span>
                      <span className={`mx-2 text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>·</span>
                      <span className="text-xs">{meta ? (bg ? meta.name_bg : meta.name_en) : f.condition_id}</span>
                    </div>
                    <Button onClick={() => handleDeleteFamily(f.id)} variant="ghost" size="icon" className="h-7 w-7">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {(profile?.family_history || []).length === 0 && (
              <p className={`text-xs text-center py-6 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Не са въведени фамилни анамнези." : "No family history entered."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* FOOD RECOMMENDATIONS TAB */}
      {tab === "foods" && <FoodRecommendationsTab isDark={isDark} bg={bg} API={API} />}

      {/* AI ANALYSIS TAB */}
      {tab === "ai" && <MultiBotAnalysisTab isDark={isDark} bg={bg} API={API} hasConsent={hasConsent} openConsent={() => setShowConsent(true)} />}

      {/* LABS TAB */}
      {tab === "labs" && <LabPdfUploadTab isDark={isDark} bg={bg} API={API} hasConsent={hasConsent} openConsent={() => setShowConsent(true)} />}

      {/* GDPR consent dialog */}
      <GdprConsentDialog open={showConsent} onClose={() => setShowConsent(false)} onGranted={fetchAll} />
    </div>
  );
}

// ─── FOOD RECOMMENDATIONS ──────────────────────────────────────────────
function FoodRecommendationsTab({ isDark, bg, API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/health-intelligence/food-recommendations`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [API]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin mx-auto py-8" />;

  const noConditions = (data?.user_conditions || []).length === 0;

  return (
    <div className="space-y-3" data-testid="phm-foods-tab">
      {noConditions ? (
        <Card className={`border-dashed border-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-300 bg-slate-50/60"}`}>
          <CardContent className="p-6 text-center">
            <Leaf className={`w-8 h-8 mx-auto mb-2 opacity-40 ${isDark ? "text-slate-500" : "text-slate-500"}`} />
            <p className="text-xs">{bg ? "Добави поне 1 състояние за персонализирани препоръки." : "Add at least 1 condition for personalized recommendations."}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className={`border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <ThumbsUp className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                <h3 className="text-sm font-bold">{bg ? "Препоръчителни храни" : "Recommended foods"}</h3>
                <span className="ml-auto text-[10px] font-bold">{data?.top_foods?.length || 0}</span>
              </div>
              {data?.top_foods?.slice(0, 12).map((f, i) => (
                <div key={i} className="text-[11.5px]">
                  <strong>• {bg ? f.name_bg : f.name_en}</strong>
                  <span className={`block text-[10.5px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? f.reason_bg : f.reason_en}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className={`border ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <ThumbsDown className={`w-4 h-4 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
                <h3 className="text-sm font-bold">{bg ? "Консумирай по-рядко" : "Consume less often"}</h3>
                <span className="ml-auto text-[10px] font-bold">{data?.limit_foods?.length || 0}</span>
              </div>
              {data?.limit_foods?.slice(0, 12).map((f, i) => (
                <div key={i} className="text-[11.5px]">
                  <strong>• {bg ? f.name_bg : f.name_en}</strong>
                  <span className={`block text-[10.5px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? f.reason_bg : f.reason_en}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
      {data?.disclaimer_bg && (
        <div className={`text-[10.5px] p-2.5 rounded-lg ${isDark ? "bg-slate-900/40 text-slate-400" : "bg-slate-50 text-slate-600"}`}>
          {bg ? data.disclaimer_bg : data.disclaimer_en}
        </div>
      )}
    </div>
  );
}

// ─── MULTI-BOT AI ──────────────────────────────────────────────────────
function MultiBotAnalysisTab({ isDark, bg, API, hasConsent, openConsent }) {
  const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    if (!hasConsent) { openConsent(); return; }
    setRunning(true); setResult(null);
    try {
      const r = await axios.post(`${API}/health-intelligence/multi-bot-analysis`, { focus });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setRunning(false); }
  };

  const BOT_META = {
    nutri:  { icon: Leaf,        name: "NutriBot",  color: "from-emerald-500 to-teal-500" },
    endo:   { icon: Sparkles,    name: "EndoBot",   color: "from-violet-500 to-fuchsia-500" },
    nephro: { icon: FlaskConical,name: "NephroBot", color: "from-sky-500 to-cyan-500" },
    hemo:   { icon: Heart,       name: "HemoBot",   color: "from-rose-500 to-pink-500" },
  };

  return (
    <div className="space-y-3" data-testid="phm-ai-tab">
      <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
        <CardContent className="p-4 space-y-2">
          <h3 className="text-sm font-bold">{bg ? "Multi-Bot AI анализ" : "Multi-Bot AI analysis"}</h3>
          <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "4-те бота (NutriBot, EndoBot, NephroBot, HemoBot) ще анализират твоя профил, условия, симптоми, лаборатории и хранителна история."
              : "Our 4 bots (NutriBot, EndoBot, NephroBot, HemoBot) will analyze your profile, conditions, symptoms, labs, and meal history."}
          </p>
          <Textarea value={focus} onChange={(e) => setFocus(e.target.value)} rows={2} maxLength={500}
                    placeholder={bg ? "Опционално: фокус на анализа (напр. 'защо не свалям килограми?')" : "Optional: analysis focus"}
                    data-testid="phm-ai-focus" />
          <Button onClick={handleRun} disabled={running}
                  className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                  data-testid="phm-ai-run">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
            {bg ? "Стартирай анализ" : "Run analysis"}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="phm-ai-result">
          {(result.bots || []).map((b) => {
            const meta = BOT_META[b.bot] || BOT_META.nutri;
            const Icon = meta.icon;
            return (
              <Card key={b.bot} className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br ${meta.color} text-white`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-bold">{meta.name}</span>
                  </div>
                  <p className={`text-[11.5px] leading-relaxed whitespace-pre-wrap ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {b.insight || (b.error ? `⚠ ${b.error}` : "—")}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {result?.disclaimer_bg && (
        <div className={`text-[10.5px] p-2.5 rounded-lg ${isDark ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-900"}`}>
          ⚠ {result.disclaimer_bg}
        </div>
      )}
    </div>
  );
}

// ─── LAB PDF UPLOAD ────────────────────────────────────────────────────
function LabPdfUploadTab({ isDark, bg, API, hasConsent, openConsent }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!hasConsent) { openConsent(); e.target.value = ""; return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/health-intelligence/lab-ocr`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(bg ? "Лабораторни данни обработени ✓" : "Lab data processed ✓");
    } catch (err) {
      toast.error(err.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="phm-labs-tab">
      <Card className={`border-dashed border-2 ${isDark ? "border-violet-500/30 bg-violet-500/5" : "border-violet-300 bg-violet-50/40"}`}>
        <CardContent className="p-6 text-center">
          <Upload className={`w-8 h-8 mx-auto mb-2 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
          <h3 className="text-sm font-bold mb-1">{bg ? "Качи лабораторни (PDF / снимка)" : "Upload labs (PDF / image)"}</h3>
          <p className={`text-[11px] mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "AI автоматично разчита стойностите и ги запазва като snapshot."
              : "AI auto-reads values and saves as snapshot."}
          </p>
          <label className="inline-block">
            <input type="file" accept=".pdf,image/*" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="phm-lab-file" />
            <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer transition-colors ${
                  uploading
                    ? "opacity-50 cursor-wait bg-slate-300"
                    : "bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                }`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {bg ? "Избери файл" : "Pick file"}
            </span>
          </label>
        </CardContent>
      </Card>

      {result && (
        <Card className={`border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="phm-lab-result">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
              <h3 className="text-sm font-bold">{bg ? "Обработено успешно" : "Processed"}</h3>
              <span className={`ml-auto text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {result.pages_processed} {bg ? "стр." : "pages"}
              </span>
            </div>
            <p className={`text-[11.5px] mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {bg ? result.summary_bg : result.summary_en}
            </p>
            <div className="space-y-1">
              {(result.biomarkers || []).map((b, i) => (
                <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded text-[11px] ${isDark ? "bg-slate-900/40" : "bg-white"}`}>
                  <FlaskConical className="w-3 h-3 text-violet-500" />
                  <strong className="flex-1">{b.name}</strong>
                  <span className="font-mono">{b.value} {b.unit}</span>
                  {b.reference_range && <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>({b.reference_range})</span>}
                  {b.flag && b.flag !== "normal" && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                          b.flag === "high" ? "bg-rose-100 text-rose-700"
                                            : "bg-sky-100 text-sky-700"
                        }`}>
                      {b.flag.toUpperCase()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
